import React from "react";
import { theme, Tooltip } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { WizardStep } from "./types";

interface WizardStepperProps {
  steps: WizardStep[];
  current: number;
  reachableUpTo?: number;
  dirtySteps?: Record<number, boolean>;
  onStepClick?: (index: number) => void;
  onBack: () => void;
  onForward: () => void;
}

export default function WizardStepper({
  steps,
  current,
  reachableUpTo = Number.POSITIVE_INFINITY,
  dirtySteps = {},
  onStepClick,
  onBack,
  onForward,
}: WizardStepperProps) {
  const { token } = theme.useToken();

  const renderArrowButton = ({
    direction,
    disabled,
    onClick,
    ariaLabel,
  }: {
    direction: "left" | "right";
    disabled: boolean;
    onClick: () => void;
    ariaLabel: string;
  }) => {
    const Icon = direction === "left" ? ArrowLeftOutlined : ArrowRightOutlined;
    return (
      <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        style={{
          width: 29,
          height: 29,
          borderRadius: "50%",
          border: `1px solid ${token.colorBorder}`,
          background: token.colorBgContainer,
          color: token.colorTextTertiary,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          flexShrink: 0,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: 1,
        }}
      >
        <Icon style={{ fontSize: 15, color: token.colorTextTertiary }} />
      </button>
    );
  };

  const renderCircle = (index: number) => {
    const isActive = index === current;
    const isDone = index < current;
    const filled = isActive || isDone;
    const bg = filled ? token.colorPrimary : token.colorBgContainer;
    const color = filled ? "#fff" : token.colorTextTertiary;
    const border = filled
      ? `1px solid ${token.colorPrimary}`
      : `1px solid ${token.colorBorder}`;
    // Springy overshoot for the pop; smooth ease-out for the elegant spin.
    const SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";
    const SMOOTH = "cubic-bezier(0.22, 1, 0.36, 1)";

    const layerStyle: React.CSSProperties = {
      position: "absolute",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    };

    return (
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: "50%",
          fontSize: 12,
          fontWeight: 600,
          background: bg,
          color,
          border,
          flexShrink: 0,
          transition: "background 0.55s ease, border-color 0.55s ease",
        }}
      >
        <span
          style={{
            ...layerStyle,
            opacity: isDone ? 0 : 1,
            transform: isDone ? "scale(0.4) rotate(-90deg)" : "scale(1)",
            // Slower, gentler when the number returns (going back a step).
            transition: isDone
              ? `opacity 0.45s ease, transform 0.6s ${SMOOTH}`
              : `opacity 0.85s ease, transform 0.85s ${SMOOTH}`,
          }}
        >
          {index + 1}
        </span>
        <span
          style={{
            ...layerStyle,
            opacity: isDone ? 1 : 0,
            transform: isDone ? "rotate(0deg)" : "rotate(-360deg)",
            transition: `opacity 0.5s ease, transform 0.9s ${SMOOTH}`,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              transform: isDone ? "scale(1)" : "scale(0.2)",
              transition: `transform 0.8s ${SPRING}`,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12.5 L10 17 L19 7"
                stroke="#fff"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  strokeDasharray: 22,
                  strokeDashoffset: isDone ? 0 : 22,
                  transition: `stroke-dashoffset 0.6s ${SMOOTH} ${
                    isDone ? "0.36s" : "0s"
                  }`,
                }}
              />
            </svg>
          </span>
        </span>
        {dirtySteps[index] && (
          <span
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: token.colorWarning,
            }}
          />
        )}
      </span>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 0 16px",
      }}
    >
      {renderArrowButton({
        direction: "left",
        disabled: current === 0,
        onClick: onBack,
        ariaLabel: "Previous step",
      })}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {steps.map((step, index) => {
          const isActive = index === current;
          const reached = index <= reachableUpTo;
          const clickable = Boolean(onStepClick) && reached;
          return (
            <React.Fragment key={step.key}>
              <Tooltip
                title={reached ? "" : "You haven't reached this step yet"}
              >
                <div
                  role={clickable ? "button" : undefined}
                  onClick={clickable ? () => onStepClick?.(index) : undefined}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: clickable
                      ? "pointer"
                      : reached
                        ? "default"
                        : "not-allowed",
                    flexShrink: 0,
                  }}
                >
                  {renderCircle(index)}
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      color: isActive
                        ? token.colorText
                        : token.colorTextTertiary,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {step.title}
                  </span>
                </div>
              </Tooltip>
              {index < steps.length - 1 && (
                <span
                  style={{
                    flex: 1,
                    height: 1,
                    minWidth: 6,
                    margin: "0 6px",
                    background: token.colorBorderSecondary,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {renderArrowButton({
        direction: "right",
        disabled: current === steps.length - 1,
        onClick: onForward,
        ariaLabel: "Next step",
      })}
    </div>
  );
}
